import React, { useEffect } from 'react';

import { getCoreConfig } from '../config/config';
import { isImpersonatedUser } from '../utils/auth';
import { versions } from '../utils/versions';
import GoogleTagManager from './GoogleTagManager';
import Segment, { segmentReset, trackClick as TrackSegmentClick, trackEvent as trackSegmentEvent } from './Segment';
import { cioAnalytics, getCioAnalytics } from './customer-io';
import { sendGAEvent } from './google-analytics';

interface Props {
  children: React.ReactNode;
}

interface AnalyticsContextDefaultProperties {
  app: {
    name: string;
    version: string;
  };
}

export type TrackingProvider = 'customer-io' | 'segment' | 'google-analytics';

export interface TrackingOptions {
  providers?: TrackingProvider[];
}

const DEFAULT_PROVIDERS: TrackingProvider[] = ['customer-io', 'segment', 'google-analytics'];

export type ClickType = 'button' | 'link';

export interface EventProperties {
  name?: string;
  version?: string;
  platform?: string;
  binaryVersion?: string;
  [key: string]: unknown;
}

export async function getAnalyticsContextDefaultProperties(): Promise<AnalyticsContextDefaultProperties> {
  return {
    app: {
      name: 'helios-app',
      version: versions.getAppVersion(),
    },
  };
}

export async function getAnalyticsDefaultProperties(): Promise<EventProperties> {
  return {
    platform: getCoreConfig().platform,
    binaryVersion: await versions.getBinaryVersion(),
    ...(await getAnalyticsContextDefaultProperties())?.app,
  };
}

export async function trackEvent(
  eventName: string,
  properties?: EventProperties,
  options?: TrackingOptions,
): Promise<void> {
  if (isImpersonatedUser()) {
    console.log('Impersonated user, not tracking event:', eventName);
    return;
  }
  const providers = options?.providers ?? DEFAULT_PROVIDERS;
  const enrichedProperties = { ...properties, ...(await getAnalyticsDefaultProperties()) };

  if (providers.includes('segment')) {
    trackSegmentEvent(eventName, enrichedProperties);
  }
  if (providers.includes('customer-io')) {
    cioAnalytics.track(eventName, enrichedProperties);
  }
  if (providers.includes('google-analytics')) {
    sendGAEvent(eventName, enrichedProperties);
  }
}

export async function trackClick(
  trackingId: string,
  clickType: ClickType,
  properties?: EventProperties,
  options?: TrackingOptions,
): Promise<void> {
  if (isImpersonatedUser()) {
    console.log('Impersonated user, not tracking click:', trackingId);
    return;
  }
  const providers = options?.providers ?? DEFAULT_PROVIDERS;
  const clickProperties = {
    trackingId,
    clickType,
    ...properties,
    ...(await getAnalyticsDefaultProperties()),
  };

  if (providers.includes('segment')) {
    TrackSegmentClick(clickProperties);
  }
  if (providers.includes('customer-io')) {
    cioAnalytics.track('click', clickProperties);
  }
  if (providers.includes('google-analytics')) {
    sendGAEvent('click', clickProperties);
  }
}

export async function reset(): Promise<void> {
  segmentReset();
  cioAnalytics.reset();
}

export function trackPageView(properties?: EventProperties): void {
  if (isImpersonatedUser()) {
    console.log('Impersonated user, not tracking page view');
    return;
  }
  window.analytics?.page(properties);
  cioAnalytics.page(properties);
}

export async function identify(
  userId: string,
  traits: {
    firstName?: string;
    lastName?: string;
    email?: string;
    locale?: string;
    [key: string]: unknown;
  },
  options?: Record<string, unknown>,
): Promise<void> {
  if (isImpersonatedUser()) {
    console.log('Impersonated user, not identifying');
    return;
  }

  window.analytics?.identify(userId, traits, options);
  cioAnalytics.identify(userId, traits, options);
}

const Tracking: React.FC<Props> = ({ children }: Props) => {
  useEffect(() => {
    getCioAnalytics();
  }, []);

  return (
    <GoogleTagManager>
      <Segment>{children}</Segment>
    </GoogleTagManager>
  );
};

export default Tracking;
